import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Platform } from '@ionic/angular';
import { FirebaseMessaging } from '@awesome-cordova-plugins/firebase-messaging/ngx';
import { LocalNotifications } from '@awesome-cordova-plugins/local-notifications/ngx';
import { StorageService } from './storage.service';
import { Subscription } from 'rxjs';

/** Android FCM channel ID used for KilnLink alerts. Must match channel created in the app. */
export const ANDROID_PUSH_CHANNEL_ID = 'kilnlink_alerts';

/** Drawable name for Android status-bar / FCM notification icon (white silhouette on transparent). */
export const ANDROID_NOTIFICATION_ICON = 'notification_icon';

/** Ionic storage key for the registered push device display name on this device. */
export const PUSH_DEVICE_NAME_STORAGE_KEY = 'pushDeviceName';

/** Local-notification data.type for alerts surfaced while the app is in the foreground. */
export const PUSH_LOCAL_NOTIFICATION_TYPE = 'kilnlink_push';

/** FCM data.type for KilnLink alert pushes from claim-api. */
export const PUSH_FCM_DATA_TYPE = 'kiln_alert';

export interface ParsedPushMessage {
  title: string;
  body: string;
  data: Record<string, string>;
}

declare const window: Window & {
  cordova?: {
    plugins?: {
      notification?: { local?: CordovaLocalNotificationPlugin };
      settings?: {
        open?: (setting: string, onSuccess?: () => void, onError?: () => void) => void;
      };
    };
  };
  device?: {
    platform?: string;
    model?: string;
  };
};

/** Cordova Local Notifications plugin (katzer) - createChannel is Android-only. */
interface CordovaLocalNotificationPlugin {
  createChannel?: (options: Record<string, unknown>, callback?: () => void, scope?: unknown) => void;
}

export interface PushRegistrationResult {
  token: string;
  platform: string;
}

export type PushRegistrationOutcome =
  | { status: 'success'; token: string; platform: string }
  | { status: 'denied' }
  | { status: 'unavailable' }
  | { status: 'error'; error?: unknown };

export interface PushSetupStatus {
  isNativeDevice: boolean;
  permissionGranted: boolean | null;
  localDeviceName: string | null;
  androidLocalNotificationsGranted: boolean | null;
}

export type PushTokenRegisterFn = (token: string, deviceName: string) => Promise<unknown>;

/** Normalize FCM / APNs payloads from cordova-plugin-firebase-messaging. */
export function parsePushPayload(payload: unknown): ParsedPushMessage {
  const root = (payload && typeof payload === 'object') ? payload as Record<string, unknown> : {};
  const data = normalizePushData(root.data);

  let title = data.title || data.subject || '';
  let body = data.body || data.message || data.text || '';

  const gcm = (root.gcm && typeof root.gcm === 'object') ? root.gcm as Record<string, unknown> : undefined;
  const gcmNotification = (gcm?.notification && typeof gcm.notification === 'object')
    ? gcm.notification as Record<string, unknown>
    : (typeof gcm?.title === 'string' || typeof gcm?.body === 'string')
      ? gcm
      : undefined;
  if (typeof gcmNotification?.title === 'string') {
    title = gcmNotification.title;
  }
  if (typeof gcmNotification?.body === 'string') {
    body = gcmNotification.body;
  }

  const aps = (root.aps && typeof root.aps === 'object') ? root.aps as Record<string, unknown> : undefined;
  const alert = aps?.alert;
  if (typeof alert === 'string') {
    body = body || alert;
  } else if (alert && typeof alert === 'object') {
    const alertObj = alert as Record<string, unknown>;
    if (typeof alertObj.title === 'string') {
      title = title || alertObj.title;
    }
    if (typeof alertObj.body === 'string') {
      body = body || alertObj.body;
    }
  }

  return {
    title: title || 'KilnLink Alert',
    body: body || '',
    data,
  };
}

function normalizePushData(raw: unknown): Record<string, string> {
  if (!raw) {
    return {};
  }
  if (typeof raw === 'string') {
    try {
      return normalizePushData(JSON.parse(raw));
    } catch {
      return {};
    }
  }
  if (typeof raw !== 'object') {
    return {};
  }
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    out[key] = value == null ? '' : String(value);
  }
  return out;
}

/**
 * Manages push notification registration, token lifecycle, and incoming message handling.
 */
@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  private cachedToken: string | null = null;
  private currentDeviceName: string | null = null;
  private registrationPromise: Promise<PushRegistrationOutcome> | null = null;
  private deviceNameHydrated = false;
  private handlersInitialized = false;
  private localNotificationId = 900000;
  private androidChannelReady: Promise<void> | null = null;
  private fcmSubscriptions: Subscription[] = [];
  private serverTokenRegistrar: PushTokenRegisterFn | null = null;

  constructor(
    private storageService: StorageService,
    private router: Router,
    private platform: Platform,
    private localNotifications: LocalNotifications,
    private firebaseMessaging: FirebaseMessaging,
  ) {}

  /** True when running on a Cordova native build with Firebase Messaging available. */
  get isPushAvailable(): boolean {
    return this.platform.is('cordova');
  }

  /**
   * Registers the callback used to persist FCM tokens to the API (e.g. onTokenRefresh).
   */
  setServerTokenRegistrar(registrar: PushTokenRegisterFn | null): void {
    this.serverTokenRegistrar = registrar;
  }

  /**
   * Register foreground/background FCM listeners and local-notification tap handling.
   * Call once after Cordova platform.ready().
   */
  initializeIncomingHandlers(): void {
    if (this.handlersInitialized || !this.isPushAvailable) {
      return;
    }
    this.handlersInitialized = true;

    void this.ensureAndroidChannelCreated();

    this.fcmSubscriptions.push(
      this.firebaseMessaging.onMessage().subscribe({
        next: (payload) => {
          const message = parsePushPayload(payload);
          console.log('PushNotificationService: foreground FCM message', message);
          void this.showForegroundNotification(message);
        },
        error: (err) => console.error('PushNotificationService: onMessage error', err),
      })
    );

    this.fcmSubscriptions.push(
      this.firebaseMessaging.onBackgroundMessage().subscribe({
        next: (payload) => {
          const message = parsePushPayload(payload);
          console.log('PushNotificationService: background/tap FCM message', message);
          void this.handlePushOpen(message.data);
        },
        error: (err) => console.error('PushNotificationService: onBackgroundMessage error', err),
      })
    );

    this.fcmSubscriptions.push(
      this.firebaseMessaging.onTokenRefresh().subscribe({
        next: () => {
          void this.firebaseMessaging.getToken()
            .then((token) => {
              if (token) {
                console.log('PushNotificationService: FCM token refreshed');
                this.cachedToken = token;
                if (this.serverTokenRegistrar) {
                  void this.refreshRegisteredDeviceToken(this.serverTokenRegistrar);
                }
              }
            })
            .catch((err) => console.error('PushNotificationService: getToken after refresh failed', err));
        },
        error: (err) => console.error('PushNotificationService: onTokenRefresh error', err),
      })
    );

    void this.platform.ready().then(() => {
      this.localNotifications.on('click').subscribe((notification) => {
        const data = normalizePushData(notification?.data);
        if (data.type !== PUSH_LOCAL_NOTIFICATION_TYPE) {
          return;
        }
        void this.handlePushOpen(data);
      });
    });
  }

  /**
   * Request permission and register for push notifications.
   * Resolves with a typed outcome so callers can show actionable errors.
   */
  async requestPermissionAndRegister(_senderId?: string): Promise<PushRegistrationOutcome> {
    if (this.registrationPromise != null) {
      const existing = await this.registrationPromise;
      if (existing.status !== 'success') {
        this.registrationPromise = null;
      }
      return existing;
    }
    if (!this.isPushAvailable) {
      return { status: 'unavailable' };
    }

    this.registrationPromise = (async (): Promise<PushRegistrationOutcome> => {
      try {
        console.log('Registering for push notifications via Firebase Messaging.');
        this.ensureAndroidChannelExists();
        await this.firebaseMessaging.requestPermission({ forceShow: true });
        const token = await this.firebaseMessaging.getToken();
        if (token) {
          console.log('PushNotificationService: received FCM token');
          this.cachedToken = token;
          return { status: 'success', token, platform: this.getPlatform() };
        }
        return { status: 'denied' };
      } catch (err) {
        console.error('PushNotificationService: registration failed', err);
        if (this.isPermissionDeniedError(err)) {
          return { status: 'denied' };
        }
        return { status: 'error', error: err };
      }
    })();

    const result = await this.registrationPromise;
    if (result.status !== 'success') {
      this.registrationPromise = null;
    }
    return result;
  }

  async registerAtStartupIfAvailable(_senderId?: string): Promise<PushRegistrationOutcome> {
    if (!this.isPushAvailable) {
      return { status: 'unavailable' };
    }
    try {
      const token = await this.firebaseMessaging.getToken();
      if (token) {
        this.cachedToken = token;
        return { status: 'success', token, platform: this.getPlatform() };
      }
    } catch (err) {
      console.warn('PushNotificationService: startup token cache skipped', err);
    }
    return { status: 'unavailable' };
  }

  async getPushSetupStatus(): Promise<PushSetupStatus> {
    await this.ensureDeviceNameLoaded();
    if (!this.isPushAvailable) {
      return {
        isNativeDevice: false,
        permissionGranted: null,
        localDeviceName: this.currentDeviceName,
        androidLocalNotificationsGranted: null,
      };
    }

    await this.platform.ready();
    let permissionGranted: boolean | null = null;
    try {
      const token = await this.firebaseMessaging.getToken();
      permissionGranted = !!token;
    } catch {
      permissionGranted = false;
    }

    let androidLocalNotificationsGranted: boolean | null = null;
    if (this.getPlatform() === 'Android') {
      try {
        androidLocalNotificationsGranted = await this.localNotifications.hasPermission();
      } catch {
        androidLocalNotificationsGranted = null;
      }
    }

    return {
      isNativeDevice: true,
      permissionGranted,
      localDeviceName: this.currentDeviceName,
      androidLocalNotificationsGranted,
    };
  }

  /**
   * Open the OS notification settings for this app when supported.
   * Returns true if a settings screen was opened.
   */
  async openNotificationSettings(): Promise<boolean> {
    if (!this.isPushAvailable) {
      return false;
    }
    await this.platform.ready();
    try {
      if (this.platform.is('ios')) {
        window.location.href = 'app-settings:';
        return true;
      }
      if (this.platform.is('android')) {
        const settings = window.cordova?.plugins?.settings;
        if (settings?.open) {
          return await new Promise<boolean>((resolve) => {
            settings.open!(
              'application_details',
              () => resolve(true),
              () => resolve(false)
            );
          });
        }
      }
    } catch (err) {
      console.warn('PushNotificationService: could not open notification settings', err);
    }
    return false;
  }

  isDeviceRegisteredOnServer(
    devices: Array<{ name?: string }>,
    deviceName?: string | null
  ): boolean {
    const name = (deviceName ?? this.currentDeviceName)?.trim();
    if (!name || !devices?.length) {
      return false;
    }
    return devices.some((device) => (device.name || '').trim() === name);
  }

  async getFreshTokenForSync(): Promise<string | null> {
    if (!this.isPushAvailable) {
      return null;
    }
    if (this.cachedToken) {
      return this.cachedToken;
    }
    try {
      const token = await this.firebaseMessaging.getToken();
      if (token) {
        this.cachedToken = token;
        return token;
      }
    } catch (err) {
      console.error('PushNotificationService: getFreshTokenForSync failed', err);
    }
    return null;
  }

  /**
   * Re-register this device's FCM token with the server when a device name is stored locally.
   */
  async refreshRegisteredDeviceToken(register: PushTokenRegisterFn): Promise<boolean> {
    if (!this.isPushAvailable) {
      return false;
    }
    await this.ensureDeviceNameLoaded();
    const deviceName = this.currentDeviceName?.trim();
    if (!deviceName) {
      return false;
    }
    const token = await this.getFreshTokenForSync();
    if (!token) {
      return false;
    }
    try {
      await register(token, deviceName);
      return true;
    } catch (err) {
      console.error('PushNotificationService: refreshRegisteredDeviceToken failed', err);
      return false;
    }
  }

  getStoredToken(): string | null {
    return this.cachedToken;
  }

  getCurrentDeviceName(): string | null {
    return this.currentDeviceName;
  }

  async ensureDeviceNameLoaded(): Promise<string | null> {
    if (this.deviceNameHydrated) {
      return this.currentDeviceName;
    }
    this.deviceNameHydrated = true;
    try {
      const stored = await this.storageService.get(PUSH_DEVICE_NAME_STORAGE_KEY);
      if (typeof stored === 'string' && stored.trim()) {
        this.currentDeviceName = stored.trim();
      }
    } catch {
      // Storage unavailable; continue with in-memory value only.
    }
    return this.currentDeviceName;
  }

  suggestDefaultDeviceName(): string {
    const existing = this.currentDeviceName?.trim();
    if (existing) {
      return existing;
    }
    const model = window.device?.model?.trim();
    if (model) {
      return model;
    }
    const platform = this.getPlatform();
    if (platform === 'iOS') {
      return 'iPhone';
    }
    if (platform === 'Android') {
      return 'Android device';
    }
    return 'Mobile device';
  }

  setCurrentDeviceName(name: string | null): void {
    const trimmed = name?.trim() || null;
    this.currentDeviceName = trimmed;
    if (trimmed) {
      this.storageService.set(PUSH_DEVICE_NAME_STORAGE_KEY, trimmed);
    } else {
      this.storageService.remove(PUSH_DEVICE_NAME_STORAGE_KEY);
    }
  }

  clearStoredToken(): void {
    this.cachedToken = null;
    this.currentDeviceName = null;
    this.registrationPromise = null;
    this.deviceNameHydrated = false;
    this.storageService.remove(PUSH_DEVICE_NAME_STORAGE_KEY);
  }

  ensureAndroidChannelExists(): void {
    void this.ensureAndroidChannelCreated();
  }

  private async ensureAndroidChannelCreated(): Promise<void> {
    if (this.androidChannelReady) {
      return this.androidChannelReady;
    }
    this.androidChannelReady = this.createAndroidChannel();
    return this.androidChannelReady;
  }

  private async createAndroidChannel(): Promise<void> {
    if (this.getPlatform() !== 'Android') {
      return;
    }
    await this.platform.ready();
    const local = window.cordova?.plugins?.notification?.local;
    if (!local?.createChannel) {
      return;
    }
    await new Promise<void>((resolve) => {
      try {
        local.createChannel!(
          {
            androidChannelId: ANDROID_PUSH_CHANNEL_ID,
            androidChannelName: 'KilnLink Alerts',
            androidChannelDescription: 'KilnLink alerts and notifications',
            androidChannelImportance: 4,
            androidChannelEnableVibration: true,
          },
          () => resolve(),
          undefined
        );
      } catch (err) {
        console.warn('PushNotificationService: could not create Android channel', err);
        resolve();
      }
    });
  }

  private async ensureNotificationPermission(): Promise<boolean> {
    if (this.getPlatform() !== 'Android') {
      return true;
    }
    await this.platform.ready();
    try {
      const permitted = await this.localNotifications.hasPermission();
      if (permitted) {
        return true;
      }
      return await this.localNotifications.requestPermission();
    } catch (err) {
      console.warn('PushNotificationService: notification permission check failed', err);
      return false;
    }
  }

  private async showForegroundNotification(message: ParsedPushMessage): Promise<void> {
    if (!this.isPushAvailable) {
      return;
    }
    await this.platform.ready();
    await this.ensureAndroidChannelCreated();
    const permitted = await this.ensureNotificationPermission();
    if (!permitted) {
      console.warn('PushNotificationService: notifications not permitted; cannot show foreground alert');
      return;
    }
    this.localNotificationId = (this.localNotificationId + 1) % 2147480000 + 900000;
    const notificationData = {
      ...message.data,
      type: PUSH_LOCAL_NOTIFICATION_TYPE,
      title: message.title,
      body: message.body,
    };
    const iconRef = `res://${ANDROID_NOTIFICATION_ICON}`;
    try {
      await this.localNotifications.schedule({
        id: this.localNotificationId,
        title: message.title,
        text: message.body,
        data: notificationData,
        androidChannelId: ANDROID_PUSH_CHANNEL_ID,
        smallIcon: iconRef,
        icon: iconRef,
        trigger: { at: new Date() },
      } as Parameters<LocalNotifications['schedule']>[0]);
      console.log('PushNotificationService: scheduled foreground local notification');
    } catch (err) {
      console.warn('PushNotificationService: could not show foreground notification', err);
    }
  }

  private async handlePushOpen(data: Record<string, string>): Promise<void> {
    await this.platform.ready();
    const kilnId = data.kiln_id || data.kilnId || '';
    if (kilnId) {
      await this.router.navigate(['/alert-settings', kilnId]);
      return;
    }
    await this.router.navigate(['/landing']);
  }

  private isPermissionDeniedError(err: unknown): boolean {
    const message = err instanceof Error ? err.message : String(err ?? '');
    return /denied|permission|not authorized|not granted/i.test(message);
  }

  private getPlatform(): string {
    if (this.platform.is('ios')) {
      return 'iOS';
    }
    if (this.platform.is('android')) {
      return 'Android';
    }
    if (typeof window !== 'undefined' && window.cordova && window.device?.platform) {
      return window.device.platform;
    }
    return 'unknown';
  }
}
